/**
 * Owner: Masa Bayu
 * File: PrivateAuthorMobileDashboard.tsx
 * Date:Time: 2026-10-19 04:40
 * Description: Pokok mudah alih PRIVAT §390 (<768) — lima paparan dalam
 *   halaman (Home · Buku Saya · Penerbitan · Jualan · Analitik), bar tab bawah
 *   5 butang, toast jujur untuk tindakan tanpa hujung. Desktop §387 tidak
 *   disentuh; pemetaan Pintasan Pantas ikut kelulusan Q4.
 *   §461: bar tab bawah 5 butang kini QxBottomNav bersama — gaya bar inilah
 *   yang dijadikan standard semua halaman; SVG inline .qxpm__bottom dibuang.
 *   §462: tab Home bar bawah navigasi ke skrin home ('/') — dahulu hanya
 *   bertukar view dalaman; loceng pada header home dibuang (loceng sudah ada
 *   sebagai tab Notifikasi dalam bar yang sama).
 *   §467-C: paparan menerima `AuthorDashboardModel` (GET /v1/publisher/dashboard)
 *   — lencana notifikasi kini kiraan pesanan menunggu bayaran, bukan 3 tetap,
 *   dan tab Profil membuka halaman awam identiti sebenar.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { AuthorPublicationsPanel, DedicatedAnalyticsScreen, DedicatedSalesScreen } from '..';
import { getMarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import type { LandingIconName } from '../../home/marketplace/landing/LandingIcons';
import { QxBottomNav } from '../../marketplace/QxBottomNav';
import { PRIVATE_AUTHOR_LABELS } from './private-author-labels';
import { PRIVATE_AUTHOR_MOBILE_LABELS } from './private-author-mobile-labels';
import type { PrivateMobileBottomId, PrivateMobileViewId } from './private-author-mobile-labels';
import type { AuthorDashboardModel } from './private-author-view';
import { PrivateAuthorMobileHome } from './PrivateAuthorMobileHome';
import { PrivateAuthorMobileBooks } from './PrivateAuthorMobileBooks';
import { MobileSubHeader } from './PrivateAuthorMobileSubHeader';
import './private-author-mobile.css';
import './private-author-mobile-hero.css';
import './private-author-mobile-cards.css';
import './private-author-mobile-books.css';

const TOAST_MS = 2200;
const NOTIF_ID = 'qxpm-notifikasi';

function scrollTo(id: string): void {
  window.requestAnimationFrame(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/** Ke puncak; jsdom belum laksana window.scrollTo — log noise dihindari. */
function toTop(): void {
  try {
    window.scrollTo({ top: 0 });
  } catch {
    /* ujian jsdom */
  }
}

export interface PrivateAuthorMobileDashboardProps {
  readonly onNavigate: (path: string) => void;
  readonly model: AuthorDashboardModel;
}

export function PrivateAuthorMobileDashboard({
  onNavigate,
  model,
}: PrivateAuthorMobileDashboardProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const la = PRIVATE_AUTHOR_LABELS[profile.language];
  const ml = PRIVATE_AUTHOR_MOBILE_LABELS[profile.language];
  const [view, setView] = useState<PrivateMobileViewId>('home');
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const goHome = useCallback((): void => {
    setView('home');
    toTop();
  }, []);

  const showToast = useCallback((message: string): void => {
    if (toastTimer.current !== null) {
      window.clearTimeout(toastTimer.current);
    }
    setToast(message);
    toastTimer.current = window.setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, TOAST_MS);
  }, []);

  const showNotifications = useCallback((): void => {
    setView('home');
    scrollTo(NOTIF_ID);
  }, []);

  const back = useCallback((): void => {
    goHome();
  }, [goHome]);

  let content: ReactNode;
  if (view === 'home') {
    content = (
      <PrivateAuthorMobileHome
        onNavigate={onNavigate}
        openView={setView}
        profile={profile}
        la={la}
        ml={ml}
        model={model}
      />
    );
  } else if (view === 'buku') {
    content = (
      <PrivateAuthorMobileBooks
        onNavigate={onNavigate}
        onBack={back}
        openView={setView}
        showToast={showToast}
        profile={profile}
        la={la}
        ml={ml}
        model={model}
      />
    );
  } else if (view === 'penerbitan') {
    content = (
      <div className="qxpm__embed">
        <MobileSubHeader title={ml.bottom.penerbitan} onBack={back} backAria={ml.backAria} />
        <AuthorPublicationsPanel onNavigate={onNavigate} />
      </div>
    );
  } else if (view === 'sales') {
    content = (
      <div className="qxpm__embed">
        <MobileSubHeader title={la.tabs.jualan} onBack={back} backAria={ml.backAria} />
        <DedicatedSalesScreen onNavigate={onNavigate} />
      </div>
    );
  } else {
    content = (
      <div className="qxpm__embed">
        <MobileSubHeader title={la.tabs.analitik} onBack={back} backAria={ml.backAria} />
        <DedicatedAnalyticsScreen onNavigate={onNavigate} />
      </div>
    );
  }

  const bottomActions: Readonly<Record<PrivateMobileBottomId, () => void>> = {
    // §462 — tab Home mesti ke skrin home qxread; view 'home' dalaman kekal
    // untuk butang Back sub-paparan (goHome).
    home: () => {
      onNavigate('/');
    },
    buku: () => {
      setView('buku');
      toTop();
    },
    penerbitan: () => {
      setView('penerbitan');
      toTop();
    },
    notif: showNotifications,
    profil: () => {
      onNavigate(model.profilePath);
    },
  };
  const bottomOrder: readonly PrivateMobileBottomId[] = [
    'home',
    'buku',
    'penerbitan',
    'notif',
    'profil',
  ];
  const activeBottom: PrivateMobileBottomId =
    view === 'home'
      ? 'home'
      : view === 'buku'
        ? 'buku'
        : view === 'penerbitan'
          ? 'penerbitan'
          : 'home';

  return (
    <div className="qxpm" data-qxpm-view={view}>
      <main className="qxpm__scroll">{content}</main>
      <QxBottomNav
        ariaLabel={ml.bottomAria}
        items={bottomOrder.map((id) => ({
          id,
          icon: BOTTOM_ICON[id],
          label: ml.bottom[id],
          active: activeBottom === id,
          badge: id === 'notif' ? model.alertCount : undefined,
          onSelect: bottomActions[id],
        }))}
      />
      {toast !== null ? (
        <p className="qxpm__toast" role="status">
          {toast}
        </p>
      ) : null}
    </div>
  );
}

const BOTTOM_ICON: Readonly<Record<PrivateMobileBottomId, LandingIconName>> = {
  home: 'home',
  buku: 'book',
  penerbitan: 'upload',
  notif: 'bell',
  profil: 'user',
};
